import { useEffect, useRef, useState } from 'react';
import { DocxIcon, Dropdown, DropdownChangeEvent, DownloadIcon, PdfIcon, UndoIcon } from '@repo/atomic-components';
import { useBundleDownloadButton } from '../hooks/useBundleDownloadButton';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { OutlineIconButton } from '@/ui/components/OutlineIconButton';
import { fullWidthThemedDropdownPt } from '@/ui/components/themedDropdownPt';
import { useLocalStorageTemplateId } from '@/hooks';

// Edit these phrases to customize the generating state messages
export const BUNDLE_PDF_GENERATING_PHRASES = [
  'Gathering documents...',
  'Applying page numbers...',
  'Collecting seashells...',
  'Compiling bundle...',
];

const PHRASE_ROTATION_MS = 5000;
const PHRASE_FADE_MS = 300;

const DEFAULT_TEMPLATE_LABEL = 'Default template';

// localStorage key for remembering the user's last picked bundle-index
// template. Keyed globally per browser profile — the picker is a personal
// preference, not a per-case setting.
const INDEX_TEMPLATE_STORAGE_KEY = 'bundle_index_template_id';

export const BundleDownloadButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [phraseVisible, setPhraseVisible] = useState(true);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const phraseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Page-numbering choice for the PDF bundle. Bates numbered is the default
  // per product — most users export bundles for reference and want the
  // page numbers baked in.
  const [withBatesNumbering, setWithBatesNumbering] = useState<boolean>(true);

  const {
    isGenerating,
    isPdfReady,
    isError,
    errorMessage,
    indexErrorMessage,
    indexTemplates,
    handleGeneratePdf,
    handleDownloadPdf,
    handleDownloadIndex,
    handleReset,
  } = useBundleDownloadButton();

  // DOCX index template selection persists across page loads. Cached ids
  // that point at a deleted template are reconciled back to null (which
  // selects the built-in "Default template" — the first item in the list).
  const [selectedIndexTemplateId, setSelectedIndexTemplateId] = useLocalStorageTemplateId(
    INDEX_TEMPLATE_STORAGE_KEY,
    indexTemplates,
  );

  // Close the modal on outside click.
  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isOpen]);

  // Rotate generating phrases with a fade transition.
  useEffect(() => {
    if (!isGenerating) {
      setPhraseIndex(0);
      setPhraseVisible(true);
      if (phraseTimeoutRef.current) {
        clearTimeout(phraseTimeoutRef.current);
        phraseTimeoutRef.current = null;
      }
      return;
    }
    const interval = setInterval(() => {
      setPhraseVisible(false);
      phraseTimeoutRef.current = setTimeout(() => {
        setPhraseIndex((i) => (i + 1) % BUNDLE_PDF_GENERATING_PHRASES.length);
        setPhraseVisible(true);
        phraseTimeoutRef.current = null;
      }, PHRASE_FADE_MS);
    }, PHRASE_ROTATION_MS);
    return () => {
      clearInterval(interval);
      if (phraseTimeoutRef.current) {
        clearTimeout(phraseTimeoutRef.current);
        phraseTimeoutRef.current = null;
      }
    };
  }, [isGenerating]);

  const hasCustomIndexTemplates = indexTemplates.length > 0;

  return (
    <>
      <style>{`
        @keyframes bundle-dl-worm {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
      `}</style>

      <div className="relative" ref={wrapperRef}>
        <OutlineIconButton
          icon={<DownloadIcon />}
          onClick={() => setIsOpen((o) => !o)}
          aria-label="Download options"
          aria-expanded={isOpen}
          aria-haspopup="dialog"
        />
        {isGenerating && (
          <span className="bg-fletchers-yellow-500 absolute -top-[4px] -right-[4px] h-[8px] w-[8px] rounded-full" />
        )}
        {isPdfReady && !isGenerating && (
          <span className="bg-fletchers-green-300 absolute -top-[4px] -right-[4px] h-[8px] w-[8px] rounded-full" />
        )}

        {isOpen && (
          <div
            role="dialog"
            aria-label="Download options"
            className="border-fletchers-gray-110 bg-fletchers-white absolute top-[calc(100%+8px)] left-0 z-50 w-[400px] rounded-[8px] border shadow-lg"
          >
            {/* Header */}
            <div className="border-fletchers-gray-110 flex items-center border-b px-6 py-4">
              <div className="text-[14px] font-semibold text-black">Export</div>
            </div>

            {/* PDF bundle section */}
            <div className="border-fletchers-gray-110 border-b px-6 py-4">
              <div className="mb-1 flex items-center gap-2">
                <PdfIcon width={18} height={18} />
                <span className="text-[14px] font-semibold text-black">Bookmarked bundle</span>
              </div>
              <p className="text-fletchers-gray-200 mb-3 text-[14px] leading-5">
                Generate a PDF version and download once ready.
              </p>

              {!isGenerating && !isPdfReady && !isError && (
                <div className="flex items-center gap-2">
                  <Dropdown
                    value={withBatesNumbering ? 'bates' : 'plain'}
                    options={[
                      { label: 'Bates numbered', value: 'bates' },
                      { label: 'Plain', value: 'plain' },
                    ]}
                    onChange={(event: DropdownChangeEvent) => setWithBatesNumbering(event.value === 'bates')}
                    appendTo="self"
                    {...fullWidthThemedDropdownPt()}
                    className="flex-1"
                  />
                  <OutlineButton label="Generate" onClick={() => handleGeneratePdf(withBatesNumbering)} />
                </div>
              )}

              {isGenerating && (
                <div className="space-y-1.5">
                  <div className="bg-fletchers-gray-110 relative h-[3px] w-full overflow-hidden rounded-full">
                    <div
                      className="bg-fletchers-green-500 absolute inset-y-0 left-0 w-1/3 rounded-full"
                      style={{ animation: 'bundle-dl-worm 1.5s ease-in-out infinite' }}
                    />
                  </div>
                  <span
                    className="text-fletchers-gray-200 block text-[14px] transition-opacity duration-300"
                    style={{ opacity: phraseVisible ? 1 : 0 }}
                  >
                    {BUNDLE_PDF_GENERATING_PHRASES[phraseIndex]}
                  </span>
                </div>
              )}

              {isPdfReady && (
                <div className="flex items-center justify-end gap-2">
                  <OutlineButton
                    label="Reset"
                    icon={<UndoIcon className="h-[13px] w-[13px]" />}
                    className="gap-2"
                    onClick={handleReset}
                    title="Generate again"
                  />
                  <OutlineButton
                    label="Download"
                    icon={<DownloadIcon />}
                    className="gap-2"
                    onClick={handleDownloadPdf}
                  />
                </div>
              )}

              {isError && !isGenerating && (
                <div className="space-y-2">
                  <p className="text-fletchers-red-500 text-[14px]">
                    {errorMessage || 'Generation failed. Please try again.'}
                  </p>
                  <div className="flex gap-2">
                    <OutlineButton label="Retry (Plain)" onClick={() => handleGeneratePdf(false)} />
                    <OutlineButton label="Retry (Bates)" onClick={() => handleGeneratePdf(true)} />
                  </div>
                </div>
              )}
            </div>

            {/* DOCX index section */}
            <div className="px-6 pt-4 pb-6">
              <div className="mb-1 flex items-center gap-2">
                <DocxIcon width={25} height={17} />
                <span className="text-[14px] font-semibold text-black">Bundle index</span>
              </div>
              <p className="text-fletchers-gray-200 mb-3 text-[14px] leading-5">
                Select a template and download a section index
              </p>

              {!indexErrorMessage && (
                <div className="flex items-center gap-2">
                  <Dropdown
                    value={selectedIndexTemplateId}
                    options={[
                      { label: DEFAULT_TEMPLATE_LABEL, value: null },
                      ...indexTemplates.map((template) => ({
                        label: template.templateName,
                        value: template.templateId,
                      })),
                    ]}
                    onChange={(event: DropdownChangeEvent) =>
                      setSelectedIndexTemplateId((event.value as string | null) ?? null)
                    }
                    disabled={!hasCustomIndexTemplates}
                    placeholder={DEFAULT_TEMPLATE_LABEL}
                    appendTo="self"
                    {...fullWidthThemedDropdownPt()}
                    className="flex-1"
                  />
                  <OutlineButton
                    label="Download"
                    icon={<DownloadIcon />}
                    className="gap-2"
                    onClick={() => handleDownloadIndex(selectedIndexTemplateId ?? undefined)}
                  />
                </div>
              )}
              {indexErrorMessage && <p className="text-fletchers-red-500 mt-2 text-[14px]">{indexErrorMessage}</p>}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
